import React from 'react';
import { OrderFilters, OrderStatus } from '@/domain/entities';

interface OrdersFiltersProps {
  filters: OrderFilters;
  onChange: (filters: OrderFilters) => void;
  onReset: () => void;
}

const STATUSES: { value: OrderStatus; label: string }[] = [
  { value: 'pending', label: 'Ожидает' },
  { value: 'confirmed', label: 'Подтвержден' },
  { value: 'shipped', label: 'Отгружен' },
  { value: 'delivered', label: 'Доставлен' },
  { value: 'cancelled', label: 'Отменён' },
];

export const OrdersFilters: React.FC<OrdersFiltersProps> = ({
  filters,
  onChange,
  onReset,
}) => {
  const [searchInput, setSearchInput] = React.useState(filters.search || '');
  const [userIdInput, setUserIdInput] = React.useState(filters.userId || '');

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onChange({
      ...filters,
      offset: 0,
      search: searchInput.trim() || undefined,
      userId: userIdInput.trim() || undefined,
    });
  };

  const handleStatusChange = (value: string) => {
    onChange({
      ...filters,
      offset: 0,
      status: value ? (value as OrderStatus) : undefined,
    });
  };

  const handleDateChange = (key: 'dateFrom' | 'dateTo', value: string) => {
    onChange({
      ...filters,
      offset: 0,
      [key]: value || undefined,
    });
  };

  const handleReset = () => {
    setSearchInput('');
    setUserIdInput('');
    onReset();
  };

  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm p-4">
      <form onSubmit={handleSearchSubmit}>
        <div className="flex flex-col lg:flex-row gap-4 lg:items-end">
          {/* Поиск */}
          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Поиск
            </label>
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Номер заказа, имя или контакт"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400"
            />
          </div>

          {/* Статус */}
          <div className="w-full lg:w-40">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Статус
            </label>
            <select
              value={filters.status || ''}
              onChange={(e) => handleStatusChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400"
            >
              <option value="">Все статусы</option>
              {STATUSES.map((status) => (
                <option key={status.value} value={status.value}>
                  {status.label}
                </option>
              ))}
            </select>
          </div>

          {/* Пользователь ID */}
          <div className="w-full lg:w-48">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Пользователь ID
            </label>
            <input
              type="text"
              value={userIdInput}
              onChange={(e) => setUserIdInput(e.target.value)}
              placeholder="UUID пользователя"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400"
            />
          </div>

          {/* Дата от */}
          <div className="w-full lg:w-40">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Дата от
            </label>
            <input
              type="date"
              value={filters.dateFrom || ''}
              onChange={(e) => handleDateChange('dateFrom', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400"
            />
          </div>

          {/* Дата до */}
          <div className="w-full lg:w-40">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Дата до
            </label>
            <input
              type="date"
              value={filters.dateTo || ''}
              onChange={(e) => handleDateChange('dateTo', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400"
            />
          </div>

          {/* Кнопки */}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleReset}
              className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors font-medium whitespace-nowrap"
            >
              Сбросить
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors font-medium whitespace-nowrap"
            >
              Применить фильтры
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

